import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import logo from "../../assets/imgs/logo/logo_light.png";
import { FaInstagram, FaFacebook, FaYoutube } from "react-icons/fa";
import { MdAccessibilityNew } from "react-icons/md";
import { useAccessibility } from "../../context/AccessibilityContext";

const navigation = [
  { name: "Inicio", href: "/", current: false },
  {
    name: "Prefeitura",
    dropdown: [
      { name: "História", href: "/historia" },
      { name: "Gestão Municipal", href: "/governo" },
      { name: "Secretarias Municipais", href: "/secretarias" },
      { name: "Estrutura Organizacional", href: "/transparencia/estrutura-organizacional" },
    ],
  },
  { name: "Carta de Serviços", href: "/transparencia/carta-de-servicos", current: false },
  { name: "Notícias", href: "/noticias" },
  { name: "Portal da Transparência", href: "/transparencia", current: false, additionalStyle: "ring-1 ring-white/60 rounded-md" },
  {
    name: "Acessibilidade",
    icon: <MdAccessibilityNew />,
    additionalStyle: "ring-2 ring-yellow-300 bg-yellow-400/10 rounded-md",
    action: "accessibility",
  },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navbar() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { setSidebarOpen: setA11ySidebarOpen } = useAccessibility();

  return (
    <Disclosure as="nav" className="bg-blue-950">
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          {/* Mobile Menu Button */}
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          {/* Logo and Desktop Menu */}
          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <div className="flex flex-shrink-0 items-center">
              <Link to="/">
                <img
                  alt="Prefeitura Serrinha"
                  src={logo}
                  className="h-10 w-auto"
                />
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:block">
              <div className="flex space-x-4">
                {navigation.map((item) =>
                  item.dropdown ? (
                    <div key={item.name} className="group relative">
                      <button className={classNames("text-white hover:text-blue-300 montserrat px-3 py-2 text-md font-medium flex items-center gap-1", item.additionalStyle)}>
                        {item.icon && <span className="text-xl">{item.icon}</span>}
                        {item.name}
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                      <div className="absolute left-0 z-10 hidden w-48 bg-white rounded-md shadow-lg ring-1 ring-black/5 group-hover:block">
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-900"
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : item.action === "accessibility" ? (
                    <button
                      key={item.name}
                      onClick={() => setA11ySidebarOpen(true)}
                      className={classNames("text-white hover:text-blue-300 montserrat px-3 py-2 text-md font-medium flex items-center gap-1", item.additionalStyle)}
                      aria-label="Abrir configurações de acessibilidade"
                    >
                      {item.icon && <span className="text-xl">{item.icon}</span>}
                      {item.name}
                    </button>
                  ) : (
                    <a
                      key={item.name}
                      href={item.href}
                      className={classNames(
                        item.current
                          ? "text-blue-300"
                          : "text-white hover:text-blue-300",
                        "montserrat px-3 py-2 text-md font-medium",
                        item.additionalStyle,
                      )}
                    >
                      {item.name}
                    </a>
                  ),
                )}
              </div>
            </div>
          </div>

          {/* Social Media Links */}
          <div className="hidden sm:flex items-center gap-3 ml-4">
            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-pink-400 transition-colors"
              aria-label="Instagram"
            >
              <FaInstagram className="h-5 w-5" />
            </a>
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-blue-400 transition-colors"
              aria-label="Facebook"
            >
              <FaFacebook className="h-5 w-5" />
            </a>
            <a
              href="https://www.youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-red-500 transition-colors"
              aria-label="YouTube"
            >
              <FaYoutube className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-20 bg-black bg-opacity-50">
          <div className="fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-md">
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            <nav className="mt-8">
              <div className="space-y-1 px-2">
                {navigation.map((item) =>
                  item.dropdown ? (
                    <Disclosure key={item.name}>
                      <DisclosureButton className={classNames("flex w-full justify-between px-4 py-2 text-left text-base font-medium text-gray-900 hover:bg-gray-100", item.additionalStyle)}>
                        <span className="flex items-center gap-2">
                          {item.icon && <span className="text-xl">{item.icon}</span>}
                          {item.name}
                        </span>
                        <ChevronDownIcon className="h-5 w-5" />
                      </DisclosureButton>
                      <DisclosurePanel className="space-y-1 px-4">
                        {item.dropdown.map((dropdownItem) => (
                          <a
                            key={dropdownItem.name}
                            href={dropdownItem.href}
                            className="block py-1 text-sm text-gray-700 hover:bg-blue-100 hover:text-blue-900"
                          >
                            {dropdownItem.name}
                          </a>
                        ))}
                      </DisclosurePanel>
                    </Disclosure>
                  ) : item.action === "accessibility" ? (
                    <button
                      key={item.name}
                      onClick={() => { setSidebarOpen(false); setA11ySidebarOpen(true); }}
                      className={classNames("flex w-full items-center gap-2 px-4 py-2 text-base font-medium text-gray-900 hover:bg-gray-100", item.additionalStyle)}
                    >
                      {item.icon && <span className="text-xl">{item.icon}</span>}
                      {item.name}
                    </button>
                  ) : (
                    <a
                      key={item.name}
                      href={item.href}
                      className={classNames("block px-4 py-2 text-base font-medium text-gray-900 hover:bg-gray-100", item.additionalStyle)}
                    >
                      {item.name}
                    </a>
                  ),
                )}
              </div>
            </nav>
            <div className="flex items-center gap-4 px-6 py-4 border-t mt-4">
              <a
                href="https://www.instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-pink-500 transition-colors"
                aria-label="Instagram"
              >
                <FaInstagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition-colors"
                aria-label="Facebook"
              >
                <FaFacebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-red-600 transition-colors"
                aria-label="YouTube"
              >
                <FaYoutube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      )}
    </Disclosure>
  );
}
